/**
 * Cesium-free geometry and rule helpers for the globe renderer.
 *
 * Ported from loader-globe.js (coverage radius, WGS84 radius at latitude,
 * altitude-band bucketing, 90-point great-circle ring, cone segments, 2D fit,
 * TLE age rules, path lead time). The engine agent ports the same coverage
 * formulas into engine/geometry.ts; the lead dedupes later.
 */
import { ALTITUDE_BANDS, ANALYSIS, CESIUM_CONFIG, EARTH, VISUAL, type AltitudeBand } from '../constants';
import type { Antenna, Mission } from '../types';

const DEG = Math.PI / 180;

/** WGS84 geocentric radius at a geodetic latitude, in km (6378.137 at the equator, 6356.752 at the poles). */
export function wgs84RadiusAtLatKm(latDeg: number): number {
  const a = EARTH.WGS84_A_KM;
  const b = EARTH.WGS84_B_KM;
  const lat = latDeg * DEG;
  const c = Math.cos(lat);
  const s = Math.sin(lat);
  const ac = a * c;
  const bs = b * s;
  const aac = a * ac;
  const bbs = b * bs;
  return Math.sqrt((aac * aac + bbs * bbs) / (ac * ac + bs * bs));
}

/**
 * Ground coverage radius in km for a satellite at altKm seen above elevMinDeg:
 * Re * (acos(Re * cos(e) / (Re + h)) - e). Uses the WGS84 radius at latDeg when
 * given, otherwise the volumetric mean radius (the original's default for every
 * coverage circle). A falsy elevation falls back to the 5 degree analysis minimum.
 */
export function computeCoverageRadiusKm(altKm: number, elevMinDeg?: number, latDeg?: number): number {
  const elev = elevMinDeg || ANALYSIS.MIN_ELEVATION_DEG;
  const elevRad = elev * DEG;
  const re = typeof latDeg === 'number' ? wgs84RadiusAtLatKm(latDeg) : EARTH.VOLUMETRIC_MEAN_RADIUS_KM;
  return re * (Math.acos((re * Math.cos(elevRad)) / (re + altKm)) - elevRad);
}

/** The bands a mission can use: bandType plus bandTypes, de-duplicated. */
export function missionBands(mission: Pick<Mission, 'requirements'>): string[] {
  const out: string[] = [];
  const reqs = mission.requirements;
  if (!reqs) return out;
  if (reqs.bandType) out.push(reqs.bandType);
  for (const b of reqs.bandTypes ?? []) if (!out.includes(b)) out.push(b);
  return out;
}

export function bandsIntersect(antennaBands: readonly string[] | undefined, mission: Pick<Mission, 'requirements'>): boolean {
  if (!antennaBands || antennaBands.length === 0) return false;
  return missionBands(mission).some((b) => antennaBands.includes(b));
}

export interface ApplicableBand extends AltitudeBand {
  /** Highest altitude among the compatible missions bucketed into this band. */
  maxAltKmActual: number;
}

/**
 * Altitude bands that apply to an antenna: band-compatible missions at or below
 * 2000 km (L1 and deep space excluded) bucketed into ALTITUDE_BANDS, each with
 * the average altitude as representativeAltKm and the maximum as maxAltKmActual,
 * sorted by the average (loader-globe.js lines 477 to 512).
 */
export function applicableAltitudeBands(antenna: Antenna, missions: readonly Mission[]): ApplicableBand[] {
  const antennaBands = antenna.capabilities?.bands ?? [];
  const bandAltitudes = new Map<string, number[]>();

  for (const m of missions) {
    if (!m.orbit || !m.requirements) continue;
    const alt = m.orbit.altitudeKm;
    if (!alt || alt > 2000) continue;
    const type = m.orbit.type;
    if (type === 'l1' || type === 'L1' || type === 'deep_space') continue;
    if (!bandsIntersect(antennaBands, m)) continue;
    for (const band of ALTITUDE_BANDS) {
      if (alt >= band.minAltKm && alt < band.maxAltKm) {
        const list = bandAltitudes.get(band.id) ?? [];
        list.push(alt);
        bandAltitudes.set(band.id, list);
        break;
      }
    }
  }

  const result: ApplicableBand[] = [];
  for (const band of ALTITUDE_BANDS) {
    const alts = bandAltitudes.get(band.id);
    if (!alts) continue;
    const avg = alts.reduce((s, a) => s + a, 0) / alts.length;
    result.push({ ...band, representativeAltKm: avg, maxAltKmActual: Math.max(...alts) });
  }
  return result.sort((a, b) => a.representativeAltKm - b.representativeAltKm);
}

export interface RingPoint {
  lonRad: number;
  latRad: number;
}

/** Closed great-circle ring (numPoints + 1 points) of radiusKm around a point, in radians. */
export function greatCircleRing(lonDeg: number, latDeg: number, radiusKm: number, numPoints = 90): RingPoint[] {
  const re = wgs84RadiusAtLatKm(latDeg);
  const angular = radiusKm / re;
  const lat = latDeg * DEG;
  const lon = lonDeg * DEG;
  const points: RingPoint[] = [];
  for (let i = 0; i <= numPoints; i++) {
    const bearing = (2 * Math.PI * i) / numPoints;
    const pLat = Math.asin(Math.sin(lat) * Math.cos(angular) + Math.cos(lat) * Math.sin(angular) * Math.cos(bearing));
    const pLon =
      lon +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angular) * Math.cos(lat),
        Math.cos(angular) - Math.sin(lat) * Math.sin(pLat),
      );
    points.push({ lonRad: pLon, latRad: pLat });
  }
  return points;
}

export interface ConeSegment {
  index: number;
  /** Height above the antenna of the segment centre, metres. */
  midHeightM: number;
  lengthM: number;
  topRadiusM: number;
  bottomRadiusM: number;
  outline: boolean;
  numberOfVerticalLines: number;
}

/**
 * The 3D coverage dome as stacked cylinder segments. For each altitude step the
 * coverage boundary maps to a local-frame point: gamma = acos(Re cos e / (Re + h)) - e,
 * localHeight = (Re + h) cos(gamma) - Re, localRadius = (Re + h) sin(gamma), with the
 * WGS84 radius at the antenna latitude. Segments shorter than 0.1 km are padded.
 */
export function coneSegments(latDeg: number, elevMinDeg: number, maxAltKm: number, segments = 12): ConeSegment[] {
  const re = wgs84RadiusAtLatKm(latDeg);
  const elevRad = elevMinDeg * DEG;
  const local = (altKm: number) => {
    if (altKm <= 0) return { h: 0, r: 0 };
    const gamma = Math.acos((re * Math.cos(elevRad)) / (re + altKm)) - elevRad;
    return { h: (re + altKm) * Math.cos(gamma) - re, r: (re + altKm) * Math.sin(gamma) };
  };
  const out: ConeSegment[] = [];
  for (let seg = 0; seg < segments; seg++) {
    const bot = local((seg / segments) * maxAltKm);
    const top = local(((seg + 1) / segments) * maxAltKm);
    let segHeightKm = top.h - bot.h;
    if (segHeightKm < 0.1) segHeightKm = 0.1;
    out.push({
      index: seg,
      midHeightM: ((bot.h + top.h) / 2) * 1000,
      lengthM: segHeightKm * 1000,
      topRadiusM: top.r * 1000,
      bottomRadiusM: bot.r * 1000,
      outline: seg === 0 || seg === segments - 1,
      numberOfVerticalLines: seg === segments - 1 ? 16 : 0,
    });
  }
  return out;
}

export interface RectangleDegrees {
  west: number;
  south: number;
  east: number;
  north: number;
}

/** Latitude half-span the 2D view fits to the viewport height (omits the polar caps). */
export const FIT_LAT_HALF_SPAN_DEG = 70;

/**
 * The rectangle whose height locks the visible latitude span to +-70 degrees and
 * whose width matches the canvas aspect ratio, centred on centerLon. Falls back
 * to the full longitude range when the aspect-matched width would cross +-180.
 * Returns null when the canvas has no size yet.
 */
export function fit2DRectangleDegrees(canvasWidth: number, canvasHeight: number, centerLon: number): RectangleDegrees | null {
  if (!canvasWidth || !canvasHeight) return null;
  const aspect = canvasWidth / canvasHeight;
  const halfLon = Math.min(180, FIT_LAT_HALF_SPAN_DEG * aspect);
  const west = centerLon - halfLon;
  const east = centerLon + halfLon;
  if (west < -180 || east > 180) {
    return { west: -180, south: -FIT_LAT_HALF_SPAN_DEG, east: 180, north: FIT_LAT_HALF_SPAN_DEG };
  }
  return { west, south: -FIT_LAT_HALF_SPAN_DEG, east, north: FIT_LAT_HALF_SPAN_DEG };
}

/** TLE epoch (line 1 columns 19 to 32, YYDDD.dddddd) as ms since the epoch, NaN when unparsable. */
export function tleEpochMs(line1: string | undefined): number {
  if (!line1 || line1.length < 32) return NaN;
  const yy = parseInt(line1.substring(18, 20), 10);
  const doy = parseFloat(line1.substring(20, 32));
  if (Number.isNaN(yy) || Number.isNaN(doy)) return NaN;
  const fullYear = yy < 57 ? 2000 + yy : 1900 + yy;
  return Date.UTC(fullYear, 0, 1) + (doy - 1) * 86400000;
}

/** Age of a TLE in days at nowMs; Infinity when the epoch cannot be parsed. */
export function tleEpochAgeDays(line1: string | undefined, nowMs: number = Date.now()): number {
  const epoch = tleEpochMs(line1);
  if (Number.isNaN(epoch)) return Infinity;
  return (nowMs - epoch) / 86400000;
}

export interface OutlineStyle {
  color: string;
  width: number;
}

/**
 * Satellite point outline by TLE age: white 1 px under TLE_WARN_DAYS, '#ffb74d' 2 px
 * up to TLE_STALE_DAYS, '#ff6b6b' 2 px beyond; catalogue satellites get no outline.
 */
export function tleOutlineStyle(ageDays: number, isCatalog: boolean): OutlineStyle {
  const color = ageDays > VISUAL.TLE_STALE_DAYS ? '#ff6b6b' : ageDays > VISUAL.TLE_WARN_DAYS ? '#ffb74d' : '#ffffff';
  const width = isCatalog ? 0 : ageDays > VISUAL.TLE_WARN_DAYS ? 2 : 1;
  return { color, width };
}

export const DEFAULT_PERIOD_MINUTES = 90;

/** Seconds of orbit drawn ahead of a satellite: a quarter period by default. */
export function orbitPathLeadSeconds(periodMinutes: number | undefined): number {
  return (periodMinutes || DEFAULT_PERIOD_MINUTES) * 60 * CESIUM_CONFIG.ORBIT_PATH_LEAD_FRACTION;
}

/** GEO by orbit type, or by a period above 1400 minutes; GEO paths are hidden. */
export function isGeoOrbit(type: string | undefined, periodMinutes: number | undefined): boolean {
  return type === 'GEO' || (periodMinutes || DEFAULT_PERIOD_MINUTES) > 1400;
}

/** Earth footprint radius of a GEO relay (spherical R = 6371 km, loader-tdrss.js). */
export function tdrsFootprintRadiusKm(altKm: number): number {
  const r = 6371;
  const halfAngle = Math.asin(r / (r + altKm));
  return r * (Math.PI / 2 - halfAngle);
}

/** Ground-track sample step: about 240 samples per orbit, clamped to 15..60 s. */
export function groundTrackStepSeconds(periodMinutes: number): number {
  return Math.max(15, Math.min(60, (periodMinutes * 60) / 240));
}

/** Split a lon/lat track into flat [lon, lat, ...] segments wherever it crosses the antimeridian. */
export function splitAtAntimeridian(points: readonly { lon: number; lat: number }[]): number[][] {
  const segments: number[][] = [[]];
  let prevLon: number | null = null;
  for (const p of points) {
    if (prevLon !== null && Math.abs(p.lon - prevLon) > 180) segments.push([]);
    segments[segments.length - 1].push(p.lon, p.lat);
    prevLon = p.lon;
  }
  return segments;
}

/** Antenna ids whose bands intersect the mission's bands (selection fallback when no connection map entry exists). */
export function connectedByBand(mission: Pick<Mission, 'requirements'>, antennas: readonly Antenna[]): Set<string> {
  const out = new Set<string>();
  for (const a of antennas) {
    if (bandsIntersect(a.capabilities?.bands, mission)) out.add(a.id);
  }
  return out;
}

/** Yesterday's date as YYYY-MM-DD (GIBS layers lag a day). */
export function gibsDateString(nowMs: number = Date.now()): string {
  return new Date(nowMs - 86400000).toISOString().split('T')[0];
}
